import React, { ReactElement, useEffect, useState } from 'react';
import styles from './AddCard.module.less';
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import ButtonModalForm from '../ButtonModalForm';
import BillingService from '../../services/BillingService';
import { message, notification, Form, Button, Collapse } from 'antd';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconCheckCircle from '../ccx/icons/CcxIconCheckCircle';
import AppForm from '../ccx/common/AppForm';
import CcxComponentProps from '../../core/CcxComponent';
import CountryService from '../../services/CountryService';
import Country from '../../types/Country';
import AppFlagIcon from '../ccx/common/AppFlagIcon';
import BillingForm from '../deployments/services/BillingForm';
import Address from '../../types/Address';
import BillingUserData from '../../types/BillingUserData';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { getStripeErrorMessage } from '../../core/helpers';
import usePiwikPro from '../../core/hooks/usePiwikPro';
import useCoupons from '../../core/hooks/useCoupons';
import { PromoCodeCard } from './PromoCodeCard';
import AppEmpty from '../AppEmpty';
import {
    ADDING_NEW_CARD_TRIGGERED_SUCCESSFULLY,
    ADDING_NEW_CARD_TRIGGERED_FAILED,
} from '../../core/PiwikConstants';
import { userCheck } from '../../slices/user.slice';

interface Props extends CcxComponentProps {
    onSuccess: Function;
    buttonType?: string;
    drawer?: boolean;
    submitText?: string;
    setCardToken?: Function;
    submitDisabled?: boolean;
    includePromoCode?: boolean;
}

function AddCard({
    onSuccess,
    buttonType,
    drawer = true,
    submitText = 'Add',
    setCardToken,
    submitDisabled,
    includePromoCode = true,
    testId = 'AddCard',
}: Props): ReactElement {
    const { trackPageView } = usePiwikPro();
    const [loading, setLoading] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const [errorFields, setErrorFields] = useState<any>([]);
    const [form] = Form.useForm();

    const { user, subscription } = useAppSelector((state) => state.user);
    const {
        coupon,
        refresh: refreshCoupon,
        loading: loadingCoupon,
    } = useCoupons();

    const cardElementOptions = {
        style: {
            base: {
                backgroundColor: '#fff',
                padding: '4px 11px',
                fontSize: '14px',
                lineHeight: '1.5715',
                fontSmoothing: 'antialiased',
                color: 'rgba(0, 0, 0, 0.65)',
                '::placeholder': {
                    color: 'rgba(0, 0, 0, 0.25)',
                },
                iconColor: 'rgba(0, 0, 0, 0.65)',
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
                fontVariant: 'tabular-nums',
            },
            invalid: {
                color: '#f5222d',
                iconColor: '#f5222d',
            },
            complete: {},
        },
        hidePostalCode: false,
    };

    const fields = [
        {
            name: ['cardNumber'],
            testId: 'AddCardCardNumber',
            required: true,
            label: 'Card number',
            type: 'rawElement',
            rawElement: (
                <div
                    className={styles.CheckoutCardElementContainer}
                    data-testid="AddCardCardElementContainer"
                >
                    <CardNumberElement options={cardElementOptions} />
                </div>
            ),
        },
        {
            name: ['cardExpiry'],
            testId: 'AddCardCardExpiry',
            required: true,
            label: 'Expiration date',
            type: 'rawElement',
            rawElement: (
                <div
                    className={styles.CheckoutCardElementContainer}
                    data-testid="AddCardCardElementContainer"
                >
                    <CardExpiryElement options={cardElementOptions} />
                </div>
            ),
        },
        {
            name: ['cardCvc'],
            testId: 'AddCardCardCvc',
            required: true,
            label: 'CVC/CVV',
            type: 'rawElement',
            rawElement: (
                <div
                    className={styles.CheckoutCardElementContainer}
                    data-testid="AddCardCardElementContainer"
                >
                    <CardCvcElement options={cardElementOptions} />
                </div>
            ),
        },
    ];

    // Add promo code fields if enabled
    const promoFields = includePromoCode
        ? [
              {
                  name: ['coupon'],
                  testId: `${testId}PromoCode`,
                  required: false,
                  label: 'Promo code (optional)',
                  placeholder: 'Enter your promo code',
                  type: 'input',
                  tips: 'Apply a promo code to get discounts on your subscription',
              },
          ]
        : [];

    // Combine card fields with promo fields
    const allFields = [...fields, ...promoFields];

    const vatFieldsSetup = [
        {
            name: ['companyName'],
            required: false,
            label: 'Company name',
            testId: `${testId}CompanyName`,
            placeholder: 'Time travel agency Ltd.',
            type: 'input',
        },
        {
            name: ['vat'],
            required: false,
            label: 'EU VAT number',
            placeholder: 'ROU25775505',
            testId: `${testId}Vat`,
            type: 'input',
            tips: (
                <div>Only for countries in the EU for reverse charge rule</div>
            ),
        },
    ];

    const fieldsSetup = [
        {
            name: ['firstName'],
            required: true,
            label: 'First name',
            placeholder: 'Enter your first name',
            testId: `${testId}FirstName`,
            type: 'input',
            onChange: async (v: any) => {
                setErrorFields([]);
            },
        },
        {
            name: ['lastName'],
            required: true,
            label: 'Last name',
            placeholder: 'Enter your last name',
            testId: `${testId}LastName`,
            type: 'input',
            onChange: async (v: any) => {
                setErrorFields([]);
            },
        },
        {
            name: ['city'],
            required: false,
            label: 'City',
            testId: `${testId}City`,
            placeholder: 'Enter your city',
            type: 'input',
        },
        {
            name: ['state'],
            required: false,
            label: 'State',
            testId: `${testId}State`,
            placeholder: 'Enter your state',
            type: 'input',
        },
        {
            name: ['email'],
            required: false,
            label: 'Email',
            testId: `${testId}Email`,
            placeholder: 'Enter your email',
            type: 'input',
            disabled: true,
            defaultValue: user?.login,
        },
        {
            name: ['postalCode'],
            required: false,
            label: 'Postal code',
            testId: `${testId}PostalCode`,
            placeholder: 'Enter your postal code',
            type: 'input',
        },

        {
            name: ['line1'],
            required: true,
            label: 'Billing address 1',
            testId: `${testId}AddressLine1`,
            placeholder: 'Enter your billing address',
            type: 'input',
            onChange: async (v: any) => {
                setErrorFields([]);
            },
        },
        {
            name: ['country'],
            required: true,
            label: 'Country',
            testId: `${testId}Country`,
            placeholder: 'Select your country',
            type: 'select',
            options: [],
            onChange: async (v: any, o: any) => {
                setErrorFields([]);
                await form.setFieldsValue({ country: v });
            },
        },
        {
            name: ['line2'],
            required: false,
            label: 'Billing address 2',
            testId: `${testId}AddressLine2`,
            placeholder: 'Enter your billing address',
            type: 'input',
        },
    ];

    const [billingFieldsSetup, setBillingFieldsSetup] =
        useState<any>(fieldsSetup);

    const [countries, setCountries] = useState<any>([]);

    const [cardForm, setCardForm] = useState<any>(undefined);

    const [billingForm] = Form.useForm();

    const dispatch = useAppDispatch();

    useEffect(() => {
        const load = async () => {
            const response = await CountryService.listCountries();
            setCountries(response);
        };

        load();
    }, []);

    useEffect(() => {
        if (fieldsSetup && countries && countries.length > 0) {
            const newStep1 = fieldsSetup.map((f: any) => {
                if (f.name && f.name[0] === 'country') {
                    f.options = countries.map((c: Country) => {
                        return {
                            value: c.code,
                            label: c.name,
                            content: (
                                <div
                                    className={styles.AppFormFlagsSelect}
                                    data-testid={`${testId}${c.code}`}
                                >
                                    <AppFlagIcon code={c.code} />
                                    {c.name}
                                </div>
                            ),
                        };
                    });
                }
                return f;
            });
            const newFields = newStep1;
            setBillingFieldsSetup(newFields);
        }
    }, [countries]);

    const validateForm = async () => {
        if (!subscription?.valid) {
            const stepValid = await validateBillingForm();

            if (!stepValid) {
                return false;
            }
        }

        if (stripe === null) {
            throw new Error(`BUG: stripe is null`);
        }

        if (elements === null) {
            throw new Error(`BUG: stripe elements is null`);
        }

        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        if (cardNumber === null) {
            throw new Error(`BUG: stripe elements cardNumberElement is null`);
        }
        if (cardExpiry === null) {
            throw new Error(`BUG: stripe elements cardExpiryElement is null`);
        }
        if (cardCvc === null) {
            throw new Error(`BUG: stripe elements cardCvcElement is null`);
        }

        return true;
    };

    let cardToken: any = null;

    const onSubmit = async () => {
        if (stripe === null) {
            throw new Error(`BUG: stripe is null`);
        }

        if (elements === null) {
            throw new Error(`BUG: stripe elements is null`);
        }

        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        if (cardNumber === null) {
            throw new Error(`BUG: stripe elements cardNumberElement is null`);
        }
        if (cardExpiry === null) {
            throw new Error(`BUG: stripe elements cardExpiryElement is null`);
        }
        if (cardCvc === null) {
            throw new Error(`BUG: stripe elements cardCvcElement is null`);
        }

        setLoading(true);
        setErrorFields([]);

        try {
            const { error, token } = await stripe.createToken(cardNumber);
            if (error) {
                throw error; // return error to catch block
            }

            cardToken = token;
            trackPageView(ADDING_NEW_CARD_TRIGGERED_SUCCESSFULLY);
            if (setCardToken) {
                setCardToken(token);
                return;
            }
        } catch (e: any) {
            trackPageView(ADDING_NEW_CARD_TRIGGERED_FAILED);
            setErrorFields([
                {
                    errors: [e.message],
                    name: ['cardInformation'],
                },
            ]);

            message.error(e.message);
            setLoading(false);

            throw e;
        }

        if (setCardToken || subscription?.valid) {
            await addCard();
        }
    };

    const addCard = async () => {
        try {
            const intent = await BillingService.createBillingIntent(
                cardToken?.id
            );

            await BillingService.completeSetupIntent(intent);
            notification.open({
                message: 'Add Card',
                description: `New Card successfully added!`,
                icon: <CcxIconCheckCircle />,
            });

            form.resetFields();
            dispatch(userCheck());
        } catch (e) {
            notification.open({
                message: 'Add Card',
                description: `There was an error adding your Card. ${getStripeErrorMessage(
                    e
                )}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });

            console.error(e);

            throw e;
        } finally {
            setLoading(false);
        }
    };

    const onSubmitButtonClick = async () => {
        const stepValid = await validateForm();

        if (stepValid) {
            setLoading(true);
            try {
                await onSubmit();
                onSuccess && onSuccess();
            } catch (e) {
                console.log('error e=', e);
            }
            setLoading(false);
        }
    };

    const validateBillingForm = async () => {
        try {
            const a = await billingForm.validateFields();
            return true;
        } catch (e: any) {
            setErrorFields(e.errorFields);
            message.error(
                'One or more fields have errors. Please double check and try again.'
            );
            return false;
        }
    };

    const handleCreateUser = async (billingForm: any) => {
        try {
            const data = {
                address: new Address({
                    country: billingForm.getFieldValue('country'),
                    state: billingForm.getFieldValue('state'),
                    city: billingForm.getFieldValue('city'),
                    line_1: billingForm.getFieldValue('line1'),
                    line_2: billingForm.getFieldValue('line2'),
                    postal_code: billingForm.getFieldValue('postalCode'),
                }),
                coupon: form.getFieldValue('coupon'),
                userData: new BillingUserData({
                    company_name: billingForm.getFieldValue('companyName'),
                    eu_vat_id: billingForm.getFieldValue('vat'),
                    first_name: billingForm.getFieldValue('firstName'),
                    last_name: billingForm.getFieldValue('lastName'),
                }),
            };

            await BillingService.createBillingUser(data);

            notification.open({
                message: 'Add billing user',
                description: 'User added successfully.',
                icon: <CcxIconCheckCircle />,
            });
            return true;
        } catch (e: any) {
            notification.open({
                message: 'Add billing user',
                description: `There was an error adding billing user. ${e}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });
            return false;
        }
    };

    const handleSetupIntent = async (cardToken: any) => {
        try {
            const intent = await BillingService.createBillingIntent(cardToken);

            await BillingService.completeSetupIntent(intent);

            notification.open({
                message: 'Add subscription intent',
                description: 'Intent added successfully.',
                icon: <CcxIconCheckCircle />,
            });

            await createUserSubscription(billingForm, intent.paymentMethodID);
        } catch (e: any) {
            notification.open({
                message: 'Add subscription intent',
                description: `There was an error adding subscription intent. ${getStripeErrorMessage(
                    e
                )}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });
        }
    };

    const createUserSubscription = async (
        billingForm: any,
        paymentMethod: any
    ) => {
        const data = {
            address: new Address({
                country: billingForm.getFieldValue('country'),
                state: billingForm.getFieldValue('state'),
                city: billingForm.getFieldValue('city'),
                line_1: billingForm.getFieldValue('line1'),
                line_2: billingForm.getFieldValue('line2'),
                postal_code: billingForm.getFieldValue('postalCode'),
            }),
            paymentMethod,
            coupon: form.getFieldValue('coupon'),
            userData: new BillingUserData({
                company_name: billingForm.getFieldValue('companyName'),
                eu_vat_id: billingForm.getFieldValue('vat'),
                first_name: billingForm.getFieldValue('firstName'),
                last_name: billingForm.getFieldValue('lastName'),
            }),
        };

        await BillingService.createUserSubscription(data);
    };

    const onSubmitCreateUser = async () => {
        setLoading(true);
        try {
            const subscriptionUser = await handleCreateUser(billingForm);

            if (subscriptionUser) {
                await onSubmit();
            }

            if (cardToken) {
                await handleSetupIntent(cardToken.id);
            }

            // Refresh user subscription info after successful billing setup
            dispatch(userCheck());
        } catch (e) {
            console.log('error e=', e);
        }
        setLoading(false);
    };

    return drawer ? (
        <ButtonModalForm
            title="Create new credit/debit card"
            buttonText="Add new card"
            onSubmit={subscription?.valid ? onSubmit : onSubmitCreateUser}
            onSuccess={onSuccess}
            fields={allFields}
            testId="AddCard"
            formLayout="vertical"
            overideValidationFunction={validateForm}
            buttonType={buttonType}
            submitText="Create"
            cancelText="Back"
            formClassName={styles.AddCardFormFields}
            rightCol={4}
            leftCol={5}
            onFormLoad={setCardForm}
        >
            <span>
                {!subscription?.valid && (
                    <>
                        <div className={styles.AddCardBilling}>
                            <strong>Enter your billing information</strong>
                            <BillingForm
                                fields={billingFieldsSetup}
                                form={billingForm}
                                errorFields={errorFields}
                            />
                        </div>
                        <div className={styles.AddCardBilling}>
                            <strong>Enter your invoice information</strong>
                            <BillingForm
                                fields={vatFieldsSetup}
                                form={billingForm}
                                errorFields={errorFields}
                            />
                        </div>
                    </>
                )}
                <strong>Enter your card credentials</strong>
                <br />
                <br />

                {/* Promo Code Section */}
                {includePromoCode && (
                    <div
                        className={styles.AddCardBilling}
                        style={{ marginTop: 16 }}
                    >
                        <strong>Promo codes</strong>

                        {/* Collapsible section for existing promo codes */}
                        <Collapse ghost style={{ marginTop: 8 }}>
                            <Collapse.Panel
                                header={`Active promo codes ${
                                    coupon ? '(1)' : '(0)'
                                }`}
                                key="existing-promo-codes"
                            >
                                {loadingCoupon ? (
                                    <div>Loading promo codes...</div>
                                ) : coupon ? (
                                    <PromoCodeCard
                                        key={`${coupon.name}coupon`}
                                        coupon={coupon}
                                        refresh={refreshCoupon}
                                    />
                                ) : (
                                    <AppEmpty
                                        message="No active promo codes"
                                        testId={`${testId}PromoNoData`}
                                    />
                                )}
                            </Collapse.Panel>
                        </Collapse>
                        <br />
                    </div>
                )}
            </span>
        </ButtonModalForm>
    ) : (
        <Form
            layout="vertical"
            form={form}
            scrollToFirstError={true}
            className={styles.AddCard}
        >
            <AppForm
                formClassName={styles.AddCardFormFields}
                fields={allFields}
                onPressEnter={onSubmitButtonClick}
                errorFields={errorFields}
            />

            {/* Promo Code Section for non-drawer mode */}
            {includePromoCode && (
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <h4>
                        <strong>Promo codes</strong>
                    </h4>

                    {/* Collapsible section for existing promo codes */}
                    <Collapse ghost style={{ marginTop: 8 }}>
                        <Collapse.Panel
                            header={`Active promo codes ${
                                coupon ? '(1)' : '(0)'
                            }`}
                            key="existing-promo-codes"
                        >
                            {loadingCoupon ? (
                                <div>Loading promo codes...</div>
                            ) : coupon ? (
                                <PromoCodeCard
                                    key={`${coupon.name}coupon`}
                                    coupon={coupon}
                                    refresh={refreshCoupon}
                                />
                            ) : (
                                <AppEmpty
                                    message="No active promo codes"
                                    testId={`${testId}PromoNoData`}
                                />
                            )}
                        </Collapse.Panel>
                    </Collapse>
                </div>
            )}

            <Button
                onClick={onSubmitButtonClick}
                loading={loading}
                disabled={submitDisabled}
            >
                {submitText}
            </Button>
        </Form>
    );
}

export default AddCard;
